/**
 * Breadcrumb Service
 *
 * Handles all breadcrumb hierarchy resolution and construction for file navigation.
 * This service is the single source of truth for breadcrumb logic, decoupling
 * breadcrumb building from component logic.
 *
 * Key Responsibilities:
 * - Build complete breadcrumb hierarchies from partial data
 * - Resolve folder/file IDs to names via API responses
 * - Cache hierarchy data for quick lookups
 * - Construct proper navigation URLs with full parent context
 * - Handle edge cases (direct URL navigation, missing data, etc.)
 */

import { FileNavigationState } from '../utils/fileNavigationUtils';

export interface ParentChainItem {
  id: string;
  name: string;
}

export interface BreadcrumbItem {
  id: string;
  name: string;
  href: string;
  type: 'folder' | 'file';
  isCurrent?: boolean;
}

/**
 * Manages hierarchy cache and breadcrumb construction
 */
export class BreadcrumbService {
  // Cache of id -> name mappings for quick lookup
  // Populated from API responses (hierarchy field or derived from items)
  private static hierarchyCache: Map<string, string> = new Map();

  /**
   * Build breadcrumbs from navigation state and API response data
   *
   * @param navigationState - Parsed URL state (folder IDs, file IDs, etc.)
   * @param parentChain - Parent chain from API response (all ancestors)
   * @param currentFolderName - Name of current folder being viewed
   * @param selectedFileName - Name of selected file (if viewing file)
   * @param hierarchy - Optional hierarchy map from API response for quick lookups
   * @returns Array of breadcrumb items ready for rendering
   */
  static buildBreadcrumbs(
    navigationState: FileNavigationState | null,
    parentChain: ParentChainItem[] | null,
    currentFolderName?: string,
    selectedFileName?: string,
    hierarchy?: Record<string, string>
  ): BreadcrumbItem[] {
    // Start with the Files root
    const breadcrumbs: BreadcrumbItem[] = [
      {
        id: '',
        name: 'Files',
        href: '/files',
        type: 'folder',
        isCurrent: !navigationState?.folderId && !navigationState?.fileId
      }
    ];

    // If no navigation state, return just the root
    if (!navigationState || (!navigationState.folderId && !navigationState.fileId)) {
      return breadcrumbs;
    }

    // Update hierarchy cache if provided
    if (hierarchy) {
      Object.entries(hierarchy).forEach(([id, name]) => {
        this.hierarchyCache.set(id, name);
      });
    }

    // Build parent chain breadcrumbs
    if (parentChain && parentChain.length > 0) {
      parentChain.forEach((parent, index) => {
        // Build href from parents up to this point
        const hrefParts = ['/files'];
        parentChain.slice(0, index + 1).forEach(p => {
          hrefParts.push(p.id);
        });

        breadcrumbs.push({
          id: parent.id,
          name: parent.name,
          href: hrefParts.join('/'),
          type: 'folder',
          isCurrent: false
        });
      });
    }

    // Add current folder if viewing folder (and not already in parent chain)
    if (navigationState.folderId && !navigationState.isViewingFile) {
      const isAlreadyInChain = parentChain?.some(p => p.id === navigationState.folderId);

      if (!isAlreadyInChain) {
        // Get folder name: preference is currentFolderName > cache > fallback to ID
        const folderName = currentFolderName || this.hierarchyCache.get(navigationState.folderId) || 'Untitled';

        // Build href
        const hrefParts = ['/files'];
        if (navigationState.parentFolderId) {
          hrefParts.push(navigationState.parentFolderId);
        }
        hrefParts.push(navigationState.folderId);

        breadcrumbs.push({
          id: navigationState.folderId,
          name: folderName,
          href: hrefParts.join('/'),
          type: 'folder',
          isCurrent: true
        });
      } else {
        // Mark the last parent chain item as current since it's the folder we're viewing
        if (breadcrumbs.length > 0) {
          breadcrumbs[breadcrumbs.length - 1].isCurrent = true;
        }
      }
    }

    // Add file if viewing file
    if (navigationState.isViewingFile && navigationState.fileId) {
      const fileName = selectedFileName || this.hierarchyCache.get(navigationState.fileId) || 'Untitled';

      // Build href with full parent context
      const hrefParts = ['/files'];
      if (navigationState.parentFolderId) {
        hrefParts.push(navigationState.parentFolderId);
      }
      if (navigationState.folderId) {
        hrefParts.push(navigationState.folderId);
      }
      hrefParts.push(navigationState.fileId);

      breadcrumbs.push({
        id: navigationState.fileId,
        name: fileName,
        href: hrefParts.join('/') + '?type=file',
        type: 'file',
        isCurrent: true
      });
    }

    return breadcrumbs;
  }

  /**
   * Resolve breadcrumb hierarchy for direct URL navigation
   *
   * Used when a user navigates directly to a URL like /files/parent-id/folder-id.
   * We don't have the names yet, so this returns breadcrumbs with IDs as names
   * as a fallback until the API response is received.
   *
   * @param urlSegments - URL path segments (e.g., ['files', 'id1', 'id2'])
   * @returns Breadcrumb items with ID-based names (will be updated when API responds)
   */
  static resolveBreadcrumbsFromUrl(urlSegments: string[]): BreadcrumbItem[] {
    const breadcrumbs: BreadcrumbItem[] = [
      {
        id: '',
        name: 'Files',
        href: '/files',
        type: 'folder',
        isCurrent: urlSegments.length <= 1
      }
    ];

    // Skip 'files' segment
    const pathSegments = urlSegments.filter(s => s && s !== 'files');

    if (pathSegments.length === 0) {
      return breadcrumbs;
    }

    // Build breadcrumbs from URL segments
    pathSegments.forEach((segment, index) => {
      const hrefParts = ['/files', ...pathSegments.slice(0, index + 1)];
      const name = this.hierarchyCache.get(segment) || segment.substring(0, 8); // Use short ID as fallback

      breadcrumbs.push({
        id: segment,
        name: name,
        href: hrefParts.join('/'),
        type: 'folder', // Assume folder unless we know it's a file
        isCurrent: index === pathSegments.length - 1
      });
    });

    return breadcrumbs;
  }

  /**
   * Update hierarchy cache with new data
   *
   * @param id - Item ID
   * @param name - Item name
   */
  static setCacheEntry(id: string, name: string): void {
    this.hierarchyCache.set(id, name);
  }

  /**
   * Update entire hierarchy cache
   *
   * @param hierarchy - Map of id -> name
   */
  static updateHierarchyCache(hierarchy: Record<string, string>): void {
    Object.entries(hierarchy).forEach(([id, name]) => {
      this.hierarchyCache.set(id, name);
    });
  }

  /**
   * Get a name from cache
   *
   * @param id - Item ID
   * @returns Name if found, undefined otherwise
   */
  static getCachedName(id: string): string | undefined {
    return this.hierarchyCache.get(id);
  }

  /**
   * Clear hierarchy cache (useful for testing or resetting state)
   */
  static clearCache(): void {
    this.hierarchyCache.clear();
  }

  /**
   * Get current cache size (for debugging)
   */
  static getCacheSize(): number {
    return this.hierarchyCache.size;
  }

  /**
   * Build navigation URL with full parent context
   *
   * Used for constructing hrefs in breadcrumbs and navigation
   *
   * @param breadcrumbs - Array of breadcrumb items
   * @param targetIndex - Index of target breadcrumb to navigate to
   * @returns URL path to navigate to
   */
  static buildNavigationUrl(breadcrumbs: BreadcrumbItem[], targetIndex: number): string {
    if (targetIndex < 0 || targetIndex >= breadcrumbs.length) {
      return '/files';
    }

    const target = breadcrumbs[targetIndex];

    // Root
    if (target.id === '') {
      return '/files';
    }

    // Build path from breadcrumbs up to target
    const pathParts = ['/files'];
    for (let i = 1; i <= targetIndex; i++) {
      if (breadcrumbs[i].id) {
        pathParts.push(breadcrumbs[i].id);
      }
    }

    // Add query params if it's a file
    if (target.type === 'file') {
      return `${pathParts.join('/')}?type=file`;
    }

    return pathParts.join('/');
  }

  /**
   * Validate breadcrumb chain integrity
   *
   * Checks for circular references or other invalid states
   *
   * @param breadcrumbs - Array of breadcrumb items
   * @returns true if valid, false otherwise
   */
  static validateBreadcrumbs(breadcrumbs: BreadcrumbItem[]): boolean {
    if (!breadcrumbs || breadcrumbs.length === 0) {
      return false;
    }

    // Root must be first
    if (breadcrumbs[0].id !== '') {
      return false;
    }

    // Check for duplicate IDs (except root)
    const ids = new Set<string>();
    for (let i = 1; i < breadcrumbs.length; i++) {
      const id = breadcrumbs[i].id;
      if (ids.has(id)) {
        return false; // Circular reference or duplicate
      }
      ids.add(id);
    }

    return true;
  }
}
